import React, { useState, useEffect } from "react";
import { ArrowBack } from "@mui/icons-material";

interface Option {
  text: string;
  icon: React.ElementType;
}

interface QuestionCardProps {
  question: string;
  options: Option[];
  onAnswerOptionClick: (option: string) => void;
  onGoBack?: () => void;
  canGoBack?: boolean;
  currentQuestionIndex?: number;
  totalQuestions?: number;
}

const QuestionCard: React.FC<QuestionCardProps> = ({
  question,
  options,
  onAnswerOptionClick,
  onGoBack,
  canGoBack = false,
  currentQuestionIndex = 1,
  totalQuestions = 8,
}) => {
  const [selectedOption, setSelectedOption] = useState<string>("");
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const handleOptionClick = (option: string) => {
    setSelectedOption(option);
    setTimeout(() => {
      onAnswerOptionClick(option);
      setSelectedOption("");
    }, 300);
  };

  return (
    <div
      style={{
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        background: "linear-gradient(135deg, #667eea 0%, #152A53 100%)",
        padding: isMobile ? "1rem" : "2rem",
      }}
    >
      <div
        className="fade-in"
        style={{
          backgroundColor: "rgba(255, 255, 255, 0.95)",
          borderRadius: "24px",
          padding: isMobile ? "1.5rem" : "2.5rem",
          boxShadow: "0 20px 40px rgba(0, 0, 0, 0.1)",
          backdropFilter: "blur(10px)",
          border: "1px solid rgba(255, 255, 255, 0.2)",
          maxWidth: isMobile ? "95%" : "700px",
          width: "100%",
          margin: "0 auto",
          position: "relative",
        }}
      >
        {/* Progress indicator */}
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginBottom: isMobile ? "1.5rem" : "2rem",
            flexWrap: "wrap",
            gap: "0.5rem",
          }}
        >
          {canGoBack && (
            <button
              onClick={onGoBack}
              style={{
                padding: isMobile ? "0.4rem 0.6rem" : "0.75rem 1rem",
                backgroundColor: "rgba(255, 255, 255, 0.1)",
                color: "rgb(201, 167, 80)",
                border: "2px solid rgb(201, 167, 80)",
                borderRadius: isMobile ? "8px" : "12px",
                cursor: "pointer",
                fontSize: isMobile ? "0.75rem" : "1rem",
                fontWeight: "600",
                transition: "all 0.3s ease",
                display: "flex",
                alignItems: "center",
                gap: isMobile ? "0.25rem" : "0.5rem",
                minWidth: "auto",
                height: isMobile ? "32px" : "44px",
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor =
                  "rgba(255, 255, 255, 0.2)";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor =
                  "rgba(255, 255, 255, 0.1)";
              }}
            >
              <ArrowBack style={{ fontSize: isMobile ? "0.9rem" : "1.2rem" }} />
              {isMobile ? "" : "Back"}
            </button>
          )}

          <div
            style={{
              fontSize: isMobile ? "0.8rem" : "0.9rem",
              color: "#6b7280",
              marginLeft: canGoBack ? "auto" : "0",
              whiteSpace: "nowrap",
            }}
          >
            Question {currentQuestionIndex} of {totalQuestions}
          </div>
        </div>

        {/* Progress bar */}
        <div
          style={{
            width: "100%",
            height: "4px",
            backgroundColor: "rgba(99, 102, 241, 0.2)",
            borderRadius: "2px",
            marginBottom: isMobile ? "1.5rem" : "2rem",
            overflow: "hidden",
          }}
        >
          <div
            style={{
              width: `${(currentQuestionIndex / totalQuestions) * 100}%`,
              height: "100%",
              backgroundColor: "#c9a750ff",
              borderRadius: "2px",
              transition: "width 0.3s ease",
            }}
          />
        </div>

        <h2
          style={{
            fontFamily: "var(--font-headline)",
            fontSize: isMobile
              ? "var(--font-size-headline-mobile)"
              : "var(--font-size-headline)",
            fontWeight: "700",
            marginBottom: isMobile ? "2rem" : "2.5rem",
            color: "#1f2937",
            lineHeight: "1.3",
            textAlign: "center",
            padding: isMobile ? "0 0.5rem" : "0",
          }}
        >
          {question}
        </h2>

        {/* Grid Layout for Options - Responsive */}
        <div
          style={{
            display: "grid",
            gridTemplateColumns: isMobile ? "1fr" : "repeat(2, 1fr)",
            gap: isMobile ? "1rem" : "1.5rem",
            maxWidth: "600px",
            margin: "0 auto",
          }}
        >
          {options.map((option, index) => {
            const IconComponent = option.icon;
            return (
              <button
                key={index}
                onClick={() => handleOptionClick(option.text)}
                style={{
                  padding: isMobile ? "1.25rem" : "1.5rem",
                  backgroundColor:
                    selectedOption === option.text
                      ? "#c9a750ff"
                      : "rgba(99, 102, 241, 0.05)",
                  color: selectedOption === option.text ? "white" : "#374151",
                  border:
                    selectedOption === option.text
                      ? "2px solid #c9a750ff"
                      : "2px solid #c9a750ff",
                  borderRadius: "16px",
                  cursor: "pointer",
                  fontSize: isMobile ? "0.9rem" : "1rem",
                  fontWeight: "500",
                  transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                  textAlign: "center",
                  minHeight: isMobile ? "100px" : "120px",
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: "0.75rem",
                  transform:
                    selectedOption === option.text ? "scale(0.98)" : "scale(1)",
                  position: "relative",
                  overflow: "hidden",
                }}
              >
                {/* Option Icon */}
                <div
                  style={{
                    fontSize: isMobile ? "2rem" : "2.5rem",
                    marginBottom: "0.5rem",
                    filter:
                      selectedOption === option.text
                        ? "brightness(1.2)"
                        : "none",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <IconComponent
                    style={{
                      fontSize: isMobile ? "2rem" : "2.5rem",
                      color:
                        selectedOption === option.text ? "white" : "#c9a750ff",
                    }}
                  />
                </div>

                {/* Option Letter */}
                <div
                  style={{
                    position: "absolute",
                    top: isMobile ? "0.5rem" : "0.75rem",
                    left: isMobile ? "0.5rem" : "0.75rem",
                    width: isMobile ? "20px" : "24px",
                    height: isMobile ? "20px" : "24px",
                    borderRadius: "50%",
                    backgroundColor:
                      selectedOption === option.text
                        ? "rgba(255, 255, 255, 0.2)"
                        : "rgba(99, 102, 241, 0.1)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: isMobile ? "0.7rem" : "0.8rem",
                    fontWeight: "600",
                  }}
                >
                  {String.fromCharCode(65 + index)}
                </div>

                {/* Option Text */}
                <span
                  style={{
                    lineHeight: "1.4",
                    fontWeight: "600",
                    fontFamily: "var(--font-body)",
                    fontSize: isMobile
                      ? "var(--font-size-body-mobile)"
                      : "var(--font-size-body)",
                  }}
                >
                  {option.text}
                </span>
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default QuestionCard;
